'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Step {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

const HowItWorks = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('how-it-works');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  const steps: Step[] = [
    {
      id: 'step_browse',
      number: 'Phase I',
      title: 'Browse & Select',
      description:
        'Explore our verified inventory of premium vehicles. Filter by make, model, price, and features to find your perfect match.',
      icon: 'MagnifyingGlassIcon',
    },
    {
      id: 'step_checkout',
      number: 'Phase II',
      title: 'Secure Checkout',
      description:
        'Complete your purchase with our integrated payment system. Your transaction is protected with bank-level security.',
      icon: 'ShieldCheckIcon',
    },
    {
      id: 'step_delivery',
      number: 'Phase III',
      title: 'Delivery Arranged',
      description:
        'Choose pickup or delivery. We coordinate with the seller to get your new car to you quickly and safely.',
      icon: 'TruckIcon',
    },
  ];

  return (
    <section data-component-id="src/app/homepage/components/HowItWorks.tsx:63:4" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="63" data-component-end-line="115" data-component-file="HowItWorks.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22how-it-works%22%2C%22className%22%3A%22py-24%20md%3Apy-32%20px-6%20bg-muted%22%7D" id="how-it-works" className="py-24 md:py-32 px-6 bg-muted">
      <div data-component-id="src/app/homepage/components/HowItWorks.tsx:64:6" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="64" data-component-end-line="114" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div data-component-id="src/app/homepage/components/HowItWorks.tsx:66:8" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="66" data-component-end-line="82" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22how-it-works%22%7D"
          id="how-it-works"
          className={`flex flex-col items-center text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span data-component-id="src/app/homepage/components/HowItWorks.tsx:72:10" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="72" data-component-end-line="74" data-component-file="HowItWorks.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20uppercase%20tracking-%5B0.3em%5D%20text-muted-foreground%20font-medium%20mb-4%20block%22%2C%22textContent%22%3A%22Simple%20Process%22%7D" className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium mb-4 block">
            Simple Process
          </span>
          <h2 data-component-id="src/app/homepage/components/HowItWorks.tsx:75:10" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="75" data-component-end-line="77" data-component-file="HowItWorks.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-display%20font-light%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22How%20It%20Works%22%7D" className="text-4xl md:text-5xl font-display font-light text-foreground mb-6">
            How It Works
          </h2>
          <p data-component-id="src/app/homepage/components/HowItWorks.tsx:78:10" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="78" data-component-end-line="81" data-component-file="HowItWorks.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20text-lg%20max-w-2xl%22%2C%22textContent%22%3A%22From%20browsing%20to%20delivery%2C%20we've%20streamlined%20the%20entire%20car%20buying%20experience%20into%20three%5Cn%20%20%20%20%20%20%20%20%20%20%20%20simple%20steps.%22%7D" className="text-muted-foreground text-lg max-w-2xl">
            From browsing to delivery, we've streamlined the entire car buying experience into three
            simple steps.
          </p>
        </div>

        {/* Steps Grid */}
        <div data-component-id="src/app/homepage/components/HowItWorks.tsx:85:8" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="85" data-component-end-line="113" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div data-component-id="src/app/homepage/components/HowItWorks.tsx:87:12" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="87" data-component-end-line="111" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={step.id}
              className={`group card p-8 hover:shadow-xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Icon */}
              <div data-component-id="src/app/homepage/components/HowItWorks.tsx:95:14" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="95" data-component-end-line="97" data-component-file="HowItWorks.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-14%20h-14%20rounded-2xl%20bg-muted%20flex%20items-center%20justify-center%20mb-6%20group-hover%3Abg-primary%20group-hover%3Atext-primary-foreground%20transition-colors%22%7D" className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon data-component-id="src/app/homepage/components/HowItWorks.tsx:96:16" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="96" data-component-end-line="96" data-component-file="HowItWorks.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={step.icon as any} size={28} />
              </div>

              {/* Phase Label */}
              <span data-component-id="src/app/homepage/components/HowItWorks.tsx:100:14" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="100" data-component-end-line="102" data-component-file="HowItWorks.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22block%20text-xs%20font-bold%20text-muted-foreground%20mb-2%20uppercase%20tracking-wider%22%7D" className="block text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wider">
                {step.number}
              </span>

              {/* Title */}
              <h3 data-component-id="src/app/homepage/components/HowItWorks.tsx:105:14" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="105" data-component-end-line="107" data-component-file="HowItWorks.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-display%20font-medium%20mb-3%20text-foreground%22%7D" className="text-xl font-display font-medium mb-3 text-foreground">
                {step.title}
              </h3>

              {/* Description */}
              <p data-component-id="src/app/homepage/components/HowItWorks.tsx:110:14" data-component-path="src/app/homepage/components/HowItWorks.tsx" data-component-line="110" data-component-end-line="110" data-component-file="HowItWorks.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20leading-relaxed%22%7D" className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
